import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";
import { Box, CircularProgress, Typography, Button } from "@mui/material";

import { osmApi } from "../osmApi";
import MarkingApp from "../components/MarkingApp";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

export default function OsmMarkingPage() {
  const { scriptId } = useParams();
  const navigate = useNavigate();

  const [pdfDoc, setPdfDoc] = useState(null);
  const [originalBytes, setOriginalBytes] = useState(null);
  const [paperScheme, setPaperScheme] = useState(null);
  const [error, setError] = useState("");
  const [loadingMessage, setLoadingMessage] = useState(
    "Loading Marking Interface...",
  );

  const [examiner] = useState(
    () => localStorage.getItem("UserName") || "Evaluator",
  );

  useEffect(() => {
    let cancelled = false;
    let loadingTask = null;

    const loadPdf = async () => {
      try {
        setError("");
        setPdfDoc(null);
        setOriginalBytes(null);
        setPaperScheme(null);

        // 1. Fetch script details
        setLoadingMessage("Loading answer book details...");

        const scriptData = await osmApi.getScript(scriptId);

        if (cancelled) return;

        if (!scriptData?.paperScheme) {
          throw new Error("Paper scheme not found.");
        }

        setPaperScheme(scriptData.paperScheme);

        // 2. Fetch original PDF through backend
        setLoadingMessage("Downloading original answer book...");

        const pdfBytes = await osmApi.getOriginalPdfBytes(scriptId);

        if (cancelled) return;

        if (!pdfBytes || pdfBytes.byteLength === 0) {
          throw new Error("Downloaded PDF is empty.");
        }

        // 3. Preserve original PDF bytes
        setOriginalBytes(pdfBytes);

        // 4. Load PDF using PDF.js
        setLoadingMessage("Preparing PDF viewer...");

        const pdfData = new Uint8Array(pdfBytes.slice(0));

        loadingTask = pdfjsLib.getDocument({
          data: pdfData,
        });

        const doc = await loadingTask.promise;

        if (cancelled) {
          await loadingTask.destroy();
          return;
        }

        setPdfDoc(doc);

        console.log("PDF loaded successfully:", {
          pages: doc.numPages,
          size: pdfBytes.byteLength,
        });
      } catch (err) {
        if (cancelled) return;

        console.error("OSM PDF loading failed:", err);

        setError(err.message || "Failed to load original answer book.");
      }
    };

    loadPdf();

    return () => {
      cancelled = true;

      if (loadingTask) {
        loadingTask.destroy().catch(() => {});
      }
    };
  }, [scriptId]);

  if (error) {
    return (
      <Box p={5} textAlign="center">
        <Typography color="error" mb={2}>
          {error}
        </Typography>

        <Button variant="contained" onClick={() => navigate("/osm")}>
          Back to List
        </Button>
      </Box>
    );
  }

  if (!pdfDoc || !originalBytes || !paperScheme) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="80vh"
      >
        <CircularProgress />

        <Typography mt={2} color="text.secondary">
          {loadingMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <MarkingApp
      scriptId={scriptId}
      examiner={examiner}
      pdfDoc={pdfDoc}
      originalBytes={originalBytes}
      paperScheme={paperScheme}
    />
  );
}
